//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  IBasicCuralateSession,
  ICuralateSession,
} from '@app/basic-session/ICuralateSession';
import { crl8, isAuthedSession } from '@app/curalate.globals';
import { SessionMode } from '@moderation-mode/constants/SessionMode';
import {
  deriveSessionMode,
  getIsModerationMergeEnabled,
  getIsModeratorCapable,
} from '@moderation-mode/moderationModeDerivation';
import { readStoredMode } from '@moderation-mode/moderationModeStorage';

type MaybeSession = ICuralateSession | IBasicCuralateSession | undefined;

const getIsCuralateEmployee = (session: ICuralateSession): boolean =>
  session.internalInfo.isCuralateEmployee;

/**
 * Impure boundary that reads the live session + persisted mode. Narrows with
 * isAuthedSession first: a logged-out session has no client/user, so
 * getIsModerationMergeEnabled would otherwise throw.
 */
export const resolveSessionMode = (
  session: MaybeSession = crl8.session,
): SessionMode => {
  if (!isAuthedSession(session)) {
    return SessionMode.Client;
  }

  return deriveSessionMode({
    hasMergeEnabled: getIsModerationMergeEnabled(session),
    isCuralateEmployee: getIsCuralateEmployee(session),
    isModeratorCapable: getIsModeratorCapable(session.permissions),
    storedMode: readStoredMode(
      String(session.user.id),
      String(session.client.id),
    ),
  });
};

/**
 * Dispatch-boundary defence: only a merge-enabled Curalate employee may switch
 * to Moderator (independent of moderation:view); anyone else downgrades to
 * Client.
 */
export const resolveRequestedSessionMode = (
  requested: SessionMode,
  session: MaybeSession = crl8.session,
): SessionMode => {
  if (requested !== SessionMode.Moderator) {
    return requested;
  }

  const isAllowed =
    isAuthedSession(session) &&
    getIsModerationMergeEnabled(session) &&
    getIsCuralateEmployee(session);

  return isAllowed ? SessionMode.Moderator : SessionMode.Client;
};
